import { z } from 'zod';
import { validators } from '@huspy/forge/shared';
import { APPLICANT_TYPE, MORTGAGE_TYPE } from '@modules/opportunities/entities/opportunity/const';
import { t } from 'i18next';
import { convertYearsAndMonthsToTotalMonths } from '@shared/utils/date-utils';

const {
  emailFieldValidator,
  numberFieldValidator,
  textFieldValidator,
  phoneNumberValidator,
} = validators;

const isRequiredText = t('common.validation.requiredField');

const numberCustomValidation = (val: any) => val !== null && val !== undefined;

export const caseBasicInfoSchemaUAE = z.object({
  first_name: textFieldValidator,
  last_name: textFieldValidator,
  email: emailFieldValidator,
  mobile: phoneNumberValidator,
  citizen_status: textFieldValidator,
  dob: textFieldValidator,
  nationality: textFieldValidator,
  salary: numberFieldValidator.min(1).optional().refine(numberCustomValidation, isRequiredText),
  employment_status: textFieldValidator,
  selected_banks: z
    .array(
      z.object({
        id: textFieldValidator.uuid(),
        mortgageType: z.enum(
          Object.values(MORTGAGE_TYPE) as [string, ...string[]]
        ),
        submissionChannelType: z.string().nullish(),
      })
    )
    .nonempty(t(isRequiredText)),
  type_of_transaction: textFieldValidator,
  application_type: textFieldValidator,
  state: textFieldValidator,
  amount: numberFieldValidator.min(1).optional().refine(numberCustomValidation, isRequiredText),
  mortgage_years: numberFieldValidator.min(1).max(25)
    .optional()
    .refine(numberCustomValidation, isRequiredText),
  mortgage_months: z.coerce.number().min(0).max(12).nullish(),
  property_status: textFieldValidator,
  property_value: numberFieldValidator.min(1).optional().refine(numberCustomValidation, isRequiredText),
  applicant_first_name: z.string().nullish(),
  applicant_last_name: z.string().nullish(),
  applicant_email: z.string().email().nullish(),
  applicant_mobile: z.string().nullish(),
  applicant_citizen_status: z.string().nullish(),
  applicant_employment_status: z.string().nullish(),
  applicant_dob: z.string().nullish(),
  applicant_salary: z.coerce.number().nullish(),
  applicant_nationality: z.string().nullish(),
  applicant_relationship_to_main: z.string().nullish(),
// eslint-disable-next-line sonarjs/cognitive-complexity
}).superRefine((data, ctx) => {
  if (data.application_type !== APPLICANT_TYPE.mainApplicant) {
    if (!data.applicant_first_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_first_name'],
      });
    }
    if (!data.applicant_last_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_last_name'],
      });
    }
    if (!data.applicant_email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_email'],
      });
    }
    if (!data.applicant_mobile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_mobile'],
      });
    }
    if (!data.applicant_citizen_status) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_citizen_status'],
      });
    }
    if (!data.applicant_dob) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_dob'],
      });
    }
    if (!data.applicant_nationality) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_nationality'],
      });
    }
    if (!data.applicant_salary) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_salary'],
      });
    }
    if (!data.applicant_employment_status) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_employment_status'],
      });
    }
    if (!data.applicant_relationship_to_main) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isRequiredText,
        path: ['applicant_relationship_to_main'],
      });
    }
  }
  if (data.mortgage_months && data.mortgage_years) {
    const months = convertYearsAndMonthsToTotalMonths(
      data.mortgage_years,
      data.mortgage_months
    );
    if (months > 300) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The total mortgage duration must not exceed 300 months.',
        path: ['mortgage_months'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The total mortgage duration must not exceed 300 months.',
        path: ['mortgage_years'],
      });
    }
  }
});
